<template>
  <section>
    <div v-if="!loading && !hasError" class="flex flex-col items-center">
      <AlertBanner v-if="alertBanner.message" :status="alertBanner.status" class="w-full sticky">
        <span>{{ alertBanner.message }}</span>
      </AlertBanner>
      <AlertBanner v-else-if="$store.getters['adventures/adventureDraft']?.edited" :status="1" class="w-full sticky">
        <span>Your changes have been saved</span>
      </AlertBanner>
      <Details
        :breadcrumbHeader="breadcrumbHeader"
        :breadcrumbLink="breadcrumbLink"
        :adventureCategoryImageUrl="adventure.adventureCategoryImage.url"
        :title="adventure.title"
        :sitterFirstName="adventure.sitterFirstName"
        :sitterLastInitial="adventure.sitterLastInitial"
        :city="adventure.sitterLocation && adventure.sitterLocation.cityname"
        :state="adventure.sitterLocation && adventure.sitterLocation.stateabbr"
        :sitterTravelDistance="adventure.sitterMilesWillingToTravel"
        :minPayRate="adventure.minPayRate"
        :maxPayRate="adventure.maxPayRate"
        :duration="adventure.duration"
        :surcharge="adventure.surcharge"
        :ageRangeText="adventure.ageRangeText"
        :adventureCategoryName="adventure.adventureCategoryName"
        :description="adventure.description"
        :sitterPassion="adventure.sitterPassion"
        :adventureSitterSupplies="suppliesArray(adventure.adventureSitterSupplies)"
        :adventureParentSupplies="suppliesArray(adventure.adventureParentSupplies)"
        :sitterAvailability="adventure.sitterAvailability"
      >
        <Button
          v-if="!isSitter"
          value="Contact"
          size="medium"
          class="w-full mt-16 mb-8 md:w-auto"
          @click.prevent="handleContact"
        />
        <div v-else-if="isSitter && adventureBelongsToSitter" class="flex justify-between">
          <Button
            v-if="isActiveAdventure"
            value="Edit"
            size="medium"
            class="grow mr-12 mt-16 mb-8"
            @click.prevent="onClickEdit"
          />
          <Button
            :value="isActiveAdventure ? 'Inactivate' : 'Activate'"
            variant="outlined"
            size="medium"
            class="grow mt-16 mb-8"
            :class="{ 'max-w-1/2': isActiveAdventure }"
            @click.prevent="handleStatusUpdate"
          />
        </div>
      </Details>

      <div v-if="!isSitter" class="w-full px-16 md:max-w-580 md:px-0">
        <SitterContactPrompt :adventure="adventure" @clickContact="handleContact" />
        <a class="text-grey-clickable underline" :href="reportAdventureUrl">Report Adventure</a>
      </div>
    </div>

    <GenericError
      v-else-if="hasError"
      class="mt-32"
      title="We couldn't find the Adventure you're looking for"
      :buttonValue="errorDetails.buttonValue"
      :buttonHref="errorDetails.buttonHref"
    >
      {{ errorDetails.body }}
    </GenericError>

    <Popup v-model="showPopup" :popupHeader="popupObj.header" @close="closePopup">
      <p class="text-body mb-16">{{ popupObj.message }}</p>
      <Button :value="popupObj.btnValue" size="medium" class="w-full" @click.prevent="popupObj.handleClick" />
    </Popup>
  </section>
</template>

<script lang="ts">
import AlertBanner from '../../../components/alert_banner/AlertBanner.vue';
import Details from './Details.vue';
import Button from '../../../components/button/Button.vue';
import Popup from '../../../components/popup/Popup.vue';
import SitterContactPrompt from './SitterContactPrompt.vue';
import GenericError from '@/components/generic_error/GenericError.vue';
import { ADVENTURE_DETAILS_QUERY } from '../../../graphql/adventures/queries';
import { UPDATE_ADVENTURE } from '../../../graphql/adventures/mutations';
import { exampleAdventures } from '@/helpers/exampleAdventures';
import { readDomainCookie, writeDomainCookie } from '@/lib/domain_cookie';
import { sendSegmentEvent } from '../../../lib/segment';

export default {
  name: 'ViewAdventure',
  components: { AlertBanner, Details, Button, Popup, SitterContactPrompt, GenericError },
  beforeRouteLeave(to, from, next) {
    this.$store.dispatch('adventures/resetStore');
    next();
  },
  data(): any {
    return {
      loading: true,
      adventure: {},
      showPopup: false,
      popupType: '',
      isReactivated: false,
      hasError: false,
    };
  },
  computed: {
    alertBanner(): { status: number; message: string } | undefined {
      if (this.isReactivated) {
        return { status: 1, message: 'This Adventure is active. Parents will see it in their search.' };
      }
      if (!this.isActiveAdventure) {
        return { status: 5, message: 'This Adventure is inactive. It cannot be edited and Parents will not see it.' };
      }
      return { status: 1, message: '' };
    },
    isSitter(): boolean {
      return this.$store.getters['account/getAccount']?.isSitter() || false;
    },
    basicAccount(): boolean {
      const account = this.$store.getters['account/getAccount'];
      return !(account?.isPremium() || account?.isCorporate());
    },
    isActiveAdventure(): boolean {
      return this.adventure?.status === 'active';
    },
    adventureBelongsToSitter(): boolean {
      const account = this.$store.getters['account/getAccount'];
      return account?.id === this.adventure.sitterUuid;
    },
    breadcrumbHeader(): string {
      return this.isSitter ? 'Adventures' : 'Search';
    },
    breadcrumbLink(): string {
      return this.isSitter ? '/adventures' : '/adventures/search';
    },
    reportAdventureUrl(): string {
      const adventureUrl = window.location.href;
      const { sitterUuid, id } = this.adventure;
      return `https://support.sittercity.com/hc/en-us/requests/new?ticket_form_id=360000078194&related_url=${adventureUrl}&sitter_uuid=${sitterUuid}&adventure_id=${id}`;
    },
    adventureSegmentData(): any {
      const { id, title, adventureCategoryName, sitterUuid, sitterLocation } = this.adventure;
      const location = `${sitterLocation.cityname}, ${sitterLocation.stateabbr}`;

      return {
        adventure_id: id,
        adventure_title: title,
        adventure_category_name: adventureCategoryName,
        adventure_location: location,
        sitter_user_id: sitterUuid,
        parent_user_id: this.$store.getters['account/getAccount']?.id,
      };
    },
    popupObj(): any {
      if (this.popupType === 'message') {
        return {
          header: 'Messaging tip',
          message: 'Share when you need care as well as some details about your children.',
          btnValue: 'Ok',
          handleClick: () => this.openConversation(),
        };
      }
      if (this.popupType === 'inactivate') {
        return {
          header: 'Inactivate your Adventure?',
          message: 'When your Adventure is inactive, parents will not see it in their search',
          btnValue: 'Inactivate',
          handleClick: () => this.updateAdventureStatus('closed'),
        };
      }
      if (this.popupType === 'error') {
        return {
          header: 'Something went wrong',
          message: 'There was an error with processing your request. Try again or contact us if the issue continues.',
          btnValue: 'Try again',
          handleClick: () => this.handleStatusUpdate(),
        };
      }
      return '';
    },
    errorDetails() {
      if (this.isSitter) {
        return {
          body: 'Try a different Adventure or contact us if the issue continues.',
          buttonValue: 'View Adventures',
          buttonHref: '/adventures',
        };
      }
      return {
        body: 'Try searching for a different Adventure or contact us if the issue continues.',
        buttonValue: 'Search Adventures',
        buttonHref: '/adventures/search',
      };
    },
  },
  mounted() {
    const staticAdventure = exampleAdventures.find((adventure) => {
      return adventure.title.toLowerCase().split(' ').join('-') === this.$route.params.adventureId;
    });
    if (staticAdventure) {
      this.adventure = staticAdventure;
      this.loading = false;
      this.$apollo.queries.adventureDetails.skip = true;
    }
  },
  methods: {
    suppliesArray(supplies: Array<any>) {
      return supplies.map((obj) => obj.item);
    },
    setContactCta(): any {
      if (this.basicAccount) {
        writeDomainCookie('last_viewed_provider_profile_id', this.adventure.sitterId);
        writeDomainCookie('last_viewed_provider_profile_care_type', 'babysitters');
      }
    },
    handleContact(): any {
      const hasInitiatedMessage = readDomainCookie('has_initiated_adventure_message');
      if (this.basicAccount) {
        this.$router.push('/upgrade/pricing');
      } else if (hasInitiatedMessage) {
        this.openConversation();
      } else {
        this.openPopup('message');
      }
      this.adventureClicked();
    },
    onClickEdit(): any {
      this.$router.push({ path: `/adventures/${this.adventure.id}/edit` });
    },
    adventureClicked() {
      const date = new Date().toLocaleString('default', { month: 'long', day: 'numeric', year: 'numeric' });
      const { id, title, adventureCategoryName, sitterUuid, sitterLocation } = this.adventure;
      const location = `${sitterLocation.cityname}, ${sitterLocation.stateabbr}`;

      sendSegmentEvent('adventures_contact_clicked', {
        send_date: date,
        adventure_id: id,
        adventure_title: title,
        adventure_category_name: adventureCategoryName,
        adventure_location: location,
        sitter_user_id: sitterUuid,
        parent_user_id: this.$store.getters['account/getAccount']?.id,
      });

      return {
        adventure_id: id,
        adventure_title: title,
        adventure_category_name: adventureCategoryName,
        adventure_location: location,
        sitter_user_id: sitterUuid,
        parent_user_id: this.$store.getters['account/getAccount']?.id,
      };
    },
    handleStatusUpdate(): any {
      if (this.isActiveAdventure) {
        this.openPopup('inactivate');
      } else {
        this.updateAdventureStatus('active');
      }
    },
    openConversation(): any {
      sessionStorage.setItem('adventure_message_data', JSON.stringify(this.adventureSegmentData));
      writeDomainCookie('has_initiated_adventure_message', 'true');

      const { sitterUuid, sitterFirstName, sitterLastInitial, id } = this.adventure;
      const displayName = `${sitterFirstName} ${sitterLastInitial}.`;
      this.$router.push({
        path: '/messages/new',
        query: {
          uuid: sitterUuid,
          displayName,
          return_path: `/adventures/details/${id}`,
          source: 'adventure',
        },
      });
    },
    openPopup(popupType: string): any {
      this.showPopup = true;
      this.popupType = popupType;
    },
    closePopup(): any {
      this.showPopup = false;
      this.popupType = '';
    },
    async updateAdventureStatus(status) {
      try {
        await this.$apollo.mutate({
          mutation: UPDATE_ADVENTURE,
          variables: {
            updateAdventureId: this.adventure.id,
            input: { status },
          },
        });
        window.scrollTo(0, 0);
        this.closePopup();
        this.isReactivated = status === 'active';
        this.$apollo.queries.adventureDetails.refetch();
      } catch (error) {
        this.openPopup('error');
      }
    },
  },
  apollo: {
    adventureDetails: {
      query: ADVENTURE_DETAILS_QUERY,
      variables(): any {
        return {
          id: Number(this.$route.params.adventureId),
        };
      },
      fetchPolicy: 'network-only',
      result({ data }) {
        const fetchedAdventure = data?.adventureDetails?.adventure;
        const { status } = fetchedAdventure;
        this.adventure = fetchedAdventure;
        if (status === 'rejected' || (status === 'closed' && !this.adventureBelongsToSitter)) {
          this.hasError = true;
        } else {
          this.loading = false;
          this.setContactCta();
        }
      },
      error() {
        this.hasError = true;
      },
    },
  },
};
</script>
